import { Injectable } from '@angular/core';
import { catchError, from, map, type Observable, of, throwError } from 'rxjs';

import type { MicrophonePermission } from '../../models/microphone-permission.model';
import {
  MicrophonePermissionDeniedError,
  VoiceRecordingFailedError,
} from '../../models/voice-recording.model';
import type { IMicrophonePermissionProvider } from './i-microphone-permission.provider';

/** Il nome del microfono nella Permissions API: non e' nel tipo `PermissionName` di tutti i browser. */
const MICROPHONE_PERMISSION_NAME = 'microphone' as PermissionName;

/**
 * Famiglia `MediaRecorder` di `features-providers.md`: il permesso e' quello del microfono che la stessa
 * famiglia apre per registrare. Lo stato si legge dalla Permissions API, la domanda si fa con
 * `getUserMedia` — e' l'unico modo che il browser offre per chiedere il microfono.
 */
@Injectable()
export class MediaRecorderMicrophonePermissionProvider implements IMicrophonePermissionProvider {
  state(): Observable<MicrophonePermission> {
    const permissions = navigator.permissions as Permissions | undefined;
    if (!permissions?.query) return of('unknown');

    return from(permissions.query({ name: MICROPHONE_PERMISSION_NAME })).pipe(
      map((status) => status.state),
      catchError(() => of<MicrophonePermission>('unknown')),
    );
  }

  /**
   * `getUserMedia` parte qui, alla chiamata: dentro un `defer` aspetterebbe la sottoscrizione, e chi la
   * fa dopo un passaggio asincrono porterebbe la domanda fuori dal gesto che l'ha chiesta.
   */
  request(): Observable<void> {
    const opening = navigator.mediaDevices.getUserMedia({ audio: true });

    return from(opening).pipe(
      map((stream) => stream.getTracks().forEach((track) => track.stop())),
      catchError((error: unknown) =>
        throwError(() =>
          error instanceof DOMException && error.name === 'NotAllowedError'
            ? new MicrophonePermissionDeniedError()
            : new VoiceRecordingFailedError(),
        ),
      ),
    );
  }
}
